import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { api } from '../../api';

export const ContentSyndicationModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '', organization: '', email: '', lmsPlatform: '',
    scormVersion: '', timeframe: '', termsAccepted: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.termsAccepted) { setError('Please accept the terms to proceed'); return; }
    setSubmitting(true); setError('');
    try {
      await api.request('/syndication-request', { method: 'POST', body: JSON.stringify(formData) });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit request. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h3 className="text-xl font-bold text-levelup-dark mb-2">Request Submitted!</h3>
          <p className="text-gray-600 mb-6">Thank you for your interest in content syndication. Our LevelUp support team will review your request and contact you soon.</p>
          <button onClick={onClose} className="px-6 py-2 bg-levelup-accent text-levelup-darker rounded-lg font-semibold hover:bg-levelup-accent-hover transition-colors">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full my-8" data-testid="syndication-modal">
        <div className="p-6 border-b bg-levelup-dark text-white rounded-t-xl">
          <h3 className="text-xl font-bold">Request Content Syndication</h3>
          <p className="text-gray-300 text-sm mt-1">Submit your request to have LevelUp courses syndicated to your LMS</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
            <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent" placeholder="Full Name" data-testid="syndication-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
            <input type="text" required value={formData.organization} onChange={(e) => setFormData({...formData, organization: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent" placeholder="Your Organization" data-testid="syndication-org" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Point of Contact Email *</label>
            <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent" placeholder="poc@organization.com" data-testid="syndication-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">LMS Platform Name *</label>
            <input type="text" required value={formData.lmsPlatform} onChange={(e) => setFormData({...formData, lmsPlatform: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent" placeholder="e.g., Moodle, Cornerstone" data-testid="syndication-lms" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SCORM Version Supported *</label>
            <select required value={formData.scormVersion} onChange={(e) => setFormData({...formData, scormVersion: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent" data-testid="syndication-scorm">
              <option value="">Select SCORM Version</option>
              <option value="SCORM 1.2">SCORM 1.2</option>
              <option value="SCORM 2004 2nd Edition">SCORM 2004 2nd Edition</option>
              <option value="SCORM 2004 3rd Edition">SCORM 2004 3rd Edition</option>
              <option value="SCORM 2004 4th Edition">SCORM 2004 4th Edition</option>
              <option value="xAPI (Tin Can)">xAPI (Tin Can)</option>
              <option value="Not Sure">Not Sure</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Timeframe *</label>
            <select required value={formData.timeframe} onChange={(e) => setFormData({...formData, timeframe: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent" data-testid="syndication-timeframe">
              <option value="">Select Timeframe</option>
              <option value="Immediately">Immediately</option>
              <option value="Within 1 month">Within 1 month</option>
              <option value="Within 3 months">Within 3 months</option>
              <option value="Within 6 months">Within 6 months</option>
              <option value="Just exploring">Just exploring</option>
            </select>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <label className="flex items-start space-x-3">
              <input type="checkbox" checked={formData.termsAccepted} onChange={(e) => setFormData({...formData, termsAccepted: e.target.checked})}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-levelup-accent focus:ring-levelup-accent" data-testid="syndication-terms" />
              <span className="text-sm text-gray-700">
                I understand that submitting this form will allow Technofocus to share my details with Microsoft. Content syndication is subject to Microsoft approval.
              </span>
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors">Cancel</button>
            <button type="submit" disabled={submitting || !formData.termsAccepted}
              className="px-6 py-2 bg-levelup-accent text-levelup-darker rounded-lg font-semibold hover:bg-levelup-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="syndication-submit">
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
